import { Controller } from '@hotwired/stimulus'

import { commissionPercentageForPaymentMethod } from '../shared/wallet_commission_percentage'

export default class extends Controller {
  static targets = ['paymentElement', 'message', 'spinner', 'submit', 'amountInput', 'creditCommissionPercentage', 'achCommissionPercentage', 'walletPaymentMethodType', 'customFeesCalculation', 'transactionFeeType', 'transactionFlatFee', 'merchantFeePortion', 'transactionMaxCommission', 'remainingCompanyFeeCap', 'walletAmountLabel', 'commissionAmountLabel', 'commissionCheckbox', 'matchingCheckbox', 'minimumMatchingAmount', 'minimumTxnAmount', 'selectedMatchRate']

  async initialize () {
    this.updateAmount()
  }

  toggleCommission () {
    this.updateAmount()
  }

  parseMoneyTarget (targetName) {
    const target = this[`${targetName}Target`]
    if (!target) return 0
    const parsed = parseFloat(target.value)
    return Number.isNaN(parsed) ? 0 : parsed
  }

  parseOptionalMoneyTarget (targetName) {
    const target = this[`${targetName}Target`]
    if (!target) return null
    const raw = `${target.value}`.trim()
    if (raw === '') return null
    const parsed = parseFloat(raw)
    return Number.isNaN(parsed) ? null : parsed
  }

  customFeesEnabled () {
    if (!this.hasCustomFeesCalculationTarget) return false
    return String(this.customFeesCalculationTarget.value) === 'true'
  }

  effectiveCommissionPercentage () {
    let methodType = 'card'
    if (this.hasWalletPaymentMethodTypeTarget && this.walletPaymentMethodTypeTarget.value) {
      methodType = this.walletPaymentMethodTypeTarget.value
    }
    return commissionPercentageForPaymentMethod({
      paymentMethodType: methodType,
      creditCommissionPercentage: this.creditCommissionPercentageTarget.value,
      achCommissionPercentage: this.hasAchCommissionPercentageTarget
        ? this.achCommissionPercentageTarget.value
        : undefined
    })
  }

  applyFeeCaps (commissionAmount) {
    const capCandidates = []

    if (this.hasTransactionMaxCommissionTarget) {
      const maxCommission = this.parseMoneyTarget('transactionMaxCommission')
      if (maxCommission > 0) capCandidates.push(maxCommission)
    }

    if (this.hasRemainingCompanyFeeCapTarget) {
      const remainingCap = this.parseOptionalMoneyTarget('remainingCompanyFeeCap')
      if (remainingCap !== null && remainingCap >= 0) capCandidates.push(remainingCap)
    }

    if (!capCandidates.length) return commissionAmount
    return Math.min(commissionAmount, ...capCandidates)
  }

  merchantFeeRate () {
    if (!this.hasMerchantFeePortionTarget || this.merchantFeePortionTarget.value === '') return 0.02

    const portion = parseFloat(this.merchantFeePortionTarget.value)
    if (Number.isNaN(portion)) return 0.02

    return portion > 1 ? portion / 100 : portion
  }

  merchantFeeEligibleForMethod () {
    const methodType = this.hasWalletPaymentMethodTypeTarget ? this.walletPaymentMethodTypeTarget.value : 'card'
    return ['card', 'us_bank_account', 'paypal', 'creditCard', 'ach', 'payPal'].includes(methodType)
  }

  calculateCommissionAmount (payingAmount) {
    if (!(this.hasCommissionCheckboxTarget && this.commissionCheckboxTarget.checked)) {
      return 0
    }

    const commissionPercentage = this.effectiveCommissionPercentage()
    let commissionAmount = (payingAmount * commissionPercentage) / 100

    if (this.customFeesEnabled()) {
      const feeType = this.hasTransactionFeeTypeTarget ? this.transactionFeeTypeTarget.value : 'variable'
      if (feeType === 'flat') {
        commissionAmount = this.parseMoneyTarget('transactionFlatFee')
      }

      commissionAmount = this.applyFeeCaps(commissionAmount)

      if (this.merchantFeeEligibleForMethod()) {
        commissionAmount += payingAmount * this.merchantFeeRate()
      }
    }

    return commissionAmount
  }

  updateAmount () {
    const payingAmount = parseFloat(this.amountInputTarget.value)
    const minimumMatchingAmount = parseFloat(this.minimumMatchingAmountTarget.value)
    const minimumTxnAmount = parseFloat(this.minimumTxnAmountTarget.value)

    if (isNaN(payingAmount)) {
      this.showMessage('Please enter a valid amount.')
      this.resetAmountToDefault()
      return
    }

    if (payingAmount < 0.5) {
      this.showMessage('The amount should not be less than $0.5!')
      this.resetAmountToDefault()
      return
    }

    if (payingAmount < minimumTxnAmount) {
      this.submitTarget.disabled = true
      const minimumInfo = this.element.querySelector('.minimum-amount-info')
      if (minimumInfo) {
        minimumInfo.classList.remove('hidden')
      }
    } else {
      this.submitTarget.disabled = false
      const minimumInfo = this.element.querySelector('.minimum-amount-info')
      if (minimumInfo) {
        minimumInfo.classList.add('hidden')
      }
    }

    const processingFeeDisplay = (payingAmount * this.effectiveCommissionPercentage()) / 100
    let totalAmount = payingAmount

    const commissionAmount = this.calculateCommissionAmount(payingAmount)
    totalAmount += commissionAmount

    if (this.hasCommissionAmountLabelTarget) {
      const labelAmount = this.customFeesEnabled() ? commissionAmount : processingFeeDisplay
      this.commissionAmountLabelTarget.textContent = `$${labelAmount.toFixed(2)}`
    }

    if (this.hasWalletAmountLabelTarget) {
      this.walletAmountLabelTarget.textContent = `$${totalAmount.toFixed(2)}`
    }

    if (this.hasMatchingCheckboxTarget) {
      if (payingAmount < minimumMatchingAmount) {
        this.matchingCheckboxTarget.checked = false
        this.matchingCheckboxTarget.disabled = true
        if (this.hasSelectedMatchRateTarget) {
          this.selectedMatchRateTarget.disabled = true
        }

        const matchingInfo = this.element.querySelector('.matching-info')
        if (matchingInfo) {
          matchingInfo.classList.remove('hidden')
        }
      } else {
        this.matchingCheckboxTarget.disabled = false
        if (this.hasSelectedMatchRateTarget) {
          this.selectedMatchRateTarget.disabled = false
        }
        const matchingInfo = this.element.querySelector('.matching-info')
        if (matchingInfo) {
          matchingInfo.classList.add('hidden')
        }
      }
    }
  }

  resetAmountToDefault () {
    const amountInput = this.amountInputTarget
    if (amountInput) {
      amountInput.value = 0.5
      this.updateAmount()
    }
  }

  showMessage (message) {
    this.messageTarget.textContent = message
    this.messageTarget.classList.remove('hidden')
    setTimeout(() => {
      this.messageTarget.classList.add('hidden')
      this.messageTarget.textContent = ''
    }, 4000)
  }

  setLoading (isLoading) {
    this.submitTarget.disabled = isLoading
    if (isLoading === true) {
      this.submitTarget.classList.add('hidden')
    } else {
      this.submitTarget.classList.remove('hidden')
    }
    this.spinnerTarget.classList.toggle('hidden', !isLoading)
  }

  getAmount () {
    return parseFloat(this.amountInputTarget.value) || 0
  }

  isValidAmount (amount) {
    return !isNaN(amount) && amount > 0
  }
}
